import { createAsyncThunk } from '@reduxjs/toolkit';
import type { Image } from '@wix/ambassador-members-v1-member/types';
import { PrivacyStatusStatus } from '@wix/ambassador-members-v1-member/types';
import { withResolvedError } from '@wix/members-area-commons-ts';

import { WIX_STATIC_MEDIA } from '../../../constants/urls';
import * as accountApi from '../../../server/account.api';
import { unblockMember as unblockMemberApi } from '../../../server/account.api';
import type {
  AccountDataProps,
  Member,
  MemberListItem,
  ThunkApiConfig,
} from '../../../types';
import { uploadImage } from './services/uploadImage';

type Picture = {
  width?: number;
  height?: number;
  file_name?: string;
  original_fileName?: string;
};

const hasMemberAvatarChanged = (currentMember: Member, initialMember: Member) =>
  currentMember.profile.photo?.url !== initialMember.profile.photo?.url;

export const makeProfilePrivate = createAsyncThunk<void, void, ThunkApiConfig>(
  'member/makeProfilePrivate',
  async (_, { extra }) => {
    const { flowAPI, profileInfoChangeSubject } = extra;
    const { httpClient, errorHandler } = flowAPI;

    const requestFn = () => httpClient.request(accountApi.leaveCommunity());

    await withResolvedError(requestFn, errorHandler, {
      errorCodesMap: {},
    });

    profileInfoChangeSubject?.notifyObservers({
      profilePrivacy: PrivacyStatusStatus.PRIVATE,
    });
  },
);

export const makeProfilePublic = createAsyncThunk<void, void, ThunkApiConfig>(
  'member/makeProfilePublic',
  async (_, { extra }) => {
    const { flowAPI, profileInfoChangeSubject } = extra;
    const { httpClient, errorHandler } = flowAPI;

    const requestFn = () => httpClient.request(accountApi.joinCommunity());

    await withResolvedError(requestFn, errorHandler, {
      errorCodesMap: {},
    });

    profileInfoChangeSubject?.notifyObservers({
      profilePrivacy: PrivacyStatusStatus.PUBLIC,
    });
  },
);

export const updateSlug = createAsyncThunk<string, string, ThunkApiConfig>(
  'member/updateSlug',
  async (newSlug, { rejectWithValue, extra }) => {
    const {
      flowAPI: { httpClient, errorHandler },
    } = extra;

    try {
      const requestFn = () =>
        httpClient.request(accountApi.updateSlug(newSlug));

      const { data } = await withResolvedError(requestFn, errorHandler, {
        errorCodesMap: {},
      });

      return data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const unblockMember = createAsyncThunk<
  Pick<MemberListItem, 'id' | 'name'>,
  Pick<MemberListItem, 'id' | 'name'>,
  ThunkApiConfig
>('members/unblockMember', async ({ id, name }, { extra, rejectWithValue }) => {
  const {
    flowAPI: { httpClient, errorHandler },
  } = extra;

  try {
    const requestFn = () => httpClient.request(unblockMemberApi(id));

    await withResolvedError(requestFn, errorHandler, {
      errorCodesMap: {},
    });

    return { id, name };
  } catch (error: any) {
    return rejectWithValue(error.response.data);
  }
});

export const maybeUpdateAvatar = createAsyncThunk<
  { avatarPhoto: Image | undefined; mediaPlatformPicture?: Picture },
  Pick<AccountDataProps, 'currentMember'>,
  ThunkApiConfig
>(
  'member/updateAvatar',
  async ({ currentMember }, { extra: { flowAPI }, getState }) => {
    const state = getState();
    const initialMember = state.member;
    const defaultReturnData = {
      avatarPhoto: currentMember.profile.photo,
    };

    if (
      !currentMember.profile.photo?.url ||
      !hasMemberAvatarChanged(currentMember, initialMember)
    ) {
      return defaultReturnData;
    }

    const mediaPlatformPicture = await uploadImage(
      flowAPI,
      currentMember.profile.photo?.id,
      currentMember.profile.photo?.url,
    );

    if (!mediaPlatformPicture?.file_name) {
      return defaultReturnData;
    }

    const avatarPhoto = {
      url: `https://${WIX_STATIC_MEDIA}${mediaPlatformPicture.file_name}`,
      height:
        mediaPlatformPicture?.height ?? initialMember.profile.photo?.height,
      width: mediaPlatformPicture?.width ?? initialMember.profile.photo?.width,
      id: mediaPlatformPicture.file_name,
    };

    return {
      avatarPhoto,
      mediaPlatformPicture,
    };
  },
);
